import { usePageContent } from "@/hooks/usePageContent";

const audience = [
  {
    icon: "ri-plane-line",
    title: "NRI Landowners",
    desc: "Protect your asset from encroachment and generate income remotely — without flying back to India. Our team handles everything on the ground.",
    tags: ["Remote Management", "Anti-Encroachment"],
    image: "https://readdy.ai/api/search-image?query=Professional%20Indian%20diaspora%20businessperson%20working%20on%20laptop%20in%20a%20modern%20home%20office%20with%20world%20map%20on%20wall%2C%20warm%20natural%20light%20from%20large%20window%2C%20contemporary%20interior%20design%2C%20photorealistic%20lifestyle%20photography%2C%20global%20professional%20atmosphere%2C%20clean%20elegant%20aesthetic%2C%20confident%20composed%20expression&width=640&height=400&seq=who-nri-01&orientation=landscape",
  },
  {
    icon: "ri-home-heart-line",
    title: "Inheritors",
    desc: "Monetize inherited land without selling the family legacy. Convert ancestral land into a wealth-generating asset for future generations.",
    tags: ["Preserve Ownership", "Family Legacy"],
    image: "https://readdy.ai/api/search-image?query=Multi%20generational%20Indian%20family%20standing%20together%20in%20front%20of%20a%20beautiful%20ancestral%20home%20with%20traditional%20architecture%2C%20warm%20golden%20evening%20light%2C%20lush%20green%20garden%20surroundings%2C%20photorealistic%20family%20portrait%20photography%2C%20emotional%20connection%20and%20heritage%20atmosphere%2C%20rich%20warm%20tones%2C%20proud%20dignified%20expressions&width=640&height=400&seq=who-inheritors-02&orientation=landscape",
  },
  {
    icon: "ri-user-3-line",
    title: "Seniors",
    desc: "Convert asset-rich, cash-poor status into monthly cash flow for retirement. Stop relying on depleting savings — let your land work for you.",
    tags: ["Monthly Cash Flow", "Retirement Income"],
    image: "https://readdy.ai/api/search-image?query=Happy%20retired%20Indian%20couple%20enjoying%20peaceful%20morning%20tea%20on%20a%20beautiful%20veranda%20overlooking%20a%20green%20garden%2C%20warm%20soft%20sunlight%2C%20comfortable%20elegant%20traditional%20home%20setting%2C%20photorealistic%20lifestyle%20photography%2C%20serene%20content%20atmosphere%2C%20rich%20natural%20colors%2C%20dignified%20graceful%20aging%20aesthetic&width=640&height=400&seq=who-seniors-03&orientation=landscape",
  },
  {
    icon: "ri-seedling-line",
    title: "Farmers",
    desc: "Unlock the true commercial value of agricultural land. JDA and lease models can generate 10x more than seasonal crop income.",
    tags: ["Agricultural Land", "10x Income Potential"],
    image: "https://readdy.ai/api/search-image?query=Indian%20farmer%20standing%20proudly%20in%20a%20lush%20green%20agricultural%20field%20at%20golden%20hour%2C%20modern%20farming%20equipment%20in%20background%2C%20rich%20fertile%20soil%20and%20healthy%20crops%2C%20photorealistic%20agricultural%20photography%2C%20warm%20golden%20sunlight%2C%20dignified%20hardworking%20atmosphere%2C%20expansive%20field%20perspective%2C%20professional%20documentary%20style&width=640&height=400&seq=who-farmers-04&orientation=landscape",
  },
  {
    icon: "ri-briefcase-4-line",
    title: "Investors",
    desc: "Land-locked capital? Unlock it. Our platform surfaces the highest-yield monetization path for your portfolio land assets.",
    tags: ["Portfolio Optimization", "Yield Maximization"],
    image: "https://readdy.ai/api/search-image?query=Professional%20real%20estate%20investor%20analyzing%20property%20portfolio%20on%20a%20large%20digital%20tablet%20in%20a%20modern%20office%2C%20city%20skyline%20visible%20through%20floor%20to%20ceiling%20windows%2C%20warm%20afternoon%20light%2C%20photorealistic%20corporate%20lifestyle%20photography%2C%20confident%20strategic%20atmosphere%2C%20clean%20premium%20aesthetic%2C%20professional%20wealth%20management%20vibe&width=640&height=400&seq=who-investors-05&orientation=landscape",
  },
  {
    icon: "ri-bank-line",
    title: "Promoters & Trusts",
    desc: "Large land banks sitting idle? We structure JDAs and commercial developments at scale — managing compliance and partner coordination.",
    tags: ["Land Bank", "Large Scale Development"],
    image: "https://readdy.ai/api/search-image?query=Two%20senior%20business%20leaders%20in%20formal%20attire%20discussing%20large%20scale%20development%20plans%20over%20architectural%20drawings%20on%20a%20conference%20table%2C%20modern%20boardroom%20with%20city%20view%2C%20bright%20natural%20light%2C%20photorealistic%20corporate%20photography%2C%20strategic%20partnership%20atmosphere%2C%20clean%20professional%20aesthetic%2C%20confident%20decisive%20expressions&width=640&height=400&seq=who-promoters-06&orientation=landscape",
  },
];

export default function WhoIsItForSection() {
  const { getContent } = usePageContent('homepage');
  const heading = getContent('home-who-is-it-heading') || 'Who Is This For?';

  return (
    <section className="py-24 bg-background-100">
      <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
            For You
          </span>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            {heading}
          </h2>
          <p className="text-foreground-600">
            Whether you own 1,000 sq.ft or 100 acres — if it&apos;s idle, we can help monetize it.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {audience.map((a) => (
            <div
              key={a.title}
              className="rounded-2xl card-dark text-center group hover:border-primary-500/40 transition-all duration-300 h-full overflow-hidden"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                <img
                  src={a.image}
                  alt={a.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
              </div>
              <div className="p-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-6 group-hover:border-primary-500/60 transition-colors">
                  <i className={`${a.icon} text-2xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-xl text-foreground-950 mb-3">{a.title}</h3>
                <p className="text-foreground-600 leading-relaxed mb-4">{a.desc}</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {a.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full bg-background-300/50 text-foreground-500"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}